import * as React from 'react';

import {
  AddressType,
  enumSalesOrderStatus,
  enumSalesOrderType,
} from '@rematter/frontend-lib-api-client';
import { Button } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { SalesDetailLayout } from '../_layouts/SalesDetailLayout';

import { SalesOrderWizardStepContractInfo } from './SalesOrderWizardStepContractInfo';

import {
  $accountsByIdQueryLazy,
  $resetSalesOrderForm,
  $salesOrderForm,
  $salesOrderIsMissingRequired,
  $setSalesOrderForm,
  $updateSalesOrder,
  $validateSalesOrderForm,
  $validateSalesOrderFormDraft,
  SalesOrderFormState,
} from '~/modules/trade/_state/salesOrderForm.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { SalesOrderRecord } from '~/modules/trade/types/salesOrder';
import { maybeParseCalendarDate } from '~/shared/utils/dateTime';

type Props = {
  salesOrder?: SalesOrderRecord | null;
  onDiscard?: () => void;
  onSaveUpdates?: () => void;
  isSaving?: boolean;
  disabled?: boolean;
  variant?: 'default' | 'embedded';
};

const salesOrderToFormState = (
  salesOrder: SalesOrderRecord,
): SalesOrderFormState => ({
  id: salesOrder.id,
  type: enumSalesOrderType.International,
  facilityId: salesOrder.facility.id,
  salesOrderStartDate:
    maybeParseCalendarDate(salesOrder.startDate?.toString()) ?? null,
  salesOrderEndDate:
    maybeParseCalendarDate(salesOrder.endDate?.toString()) ?? null,
  salesOrderNumber: salesOrder.documentNumber,
  accountRepresentativeId: salesOrder.accountRepresentative?.id || null,
  counterpartyReferenceNumber: salesOrder.counterpartyReferenceNumber || '',
  counterpartyPurchaseOrderNumber: salesOrder.counterpartyReferenceNumber || '',
  doNotShip: salesOrder.doNotShip ?? false,

  customerName: salesOrder.account.name,
  customerId: salesOrder.account.id,
  contactId: salesOrder.contact?.id || null,
  shipToLocationId: null,
  billToLocationId: null,
  sameAsShip:
    salesOrder.shippingAddress?.streetAddress ===
      salesOrder.billingAddress?.streetAddress &&
    salesOrder.shippingAddress?.city === salesOrder.billingAddress?.city,

  shippingAddress: salesOrder.shippingAddress
    ? {
        addressType: salesOrder.shippingAddress.addressType,
        streetAddress: salesOrder.shippingAddress.streetAddress,
        city: salesOrder.shippingAddress.city,
        state: salesOrder.shippingAddress.state,
        postalCode: salesOrder.shippingAddress.postalCode,
        country: salesOrder.shippingAddress.country,
        isMailingAddress: true,
      }
    : null,
  billingAddress: salesOrder.billingAddress
    ? {
        addressType: salesOrder.billingAddress.addressType,
        streetAddress: salesOrder.billingAddress.streetAddress,
        city: salesOrder.billingAddress.city,
        state: salesOrder.billingAddress.state,
        postalCode: salesOrder.billingAddress.postalCode,
        country: salesOrder.billingAddress.country,
        isMailingAddress: true,
      }
    : null,

  paymentCurrency: salesOrder.paymentCurrency,
  paymentTerm: salesOrder.paymentTerm,
  freightTerm: salesOrder.freightTerm,

  lineItems: salesOrder.lineItems || [],

  internalNotes: salesOrder.recyclerInternalNotes || '',
  externalNotes: salesOrder.recyclerExternalNotes || '',
  completionNotes: salesOrder.completionNotes || '',
  customAttributes: salesOrder.customAttributes || {},
  weightDisplayUnit: salesOrder.weightDisplayUnit || null,

  bookings: salesOrder.bookings?.map((booking) => ({
    id: booking.id,
    bookingNumber: booking.bookingNumber,
  })),

  isSubmitting: false,
  errors: {},
});

export const SalesOrderTabContractInfo: React.FC<Props> = ({
  salesOrder,
  onDiscard,
  onSaveUpdates,
  isSaving = false,
  disabled = false,
  variant = 'default',
}) => {
  const t = useTranslations();

  // States
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [originalFormState, setOriginalFormState] =
    React.useState<SalesOrderFormState | null>(null);

  // Atoms
  const isMissingRequired = useAtomValue($salesOrderIsMissingRequired);
  const { data: accountsQueryData, fetch: fetchAccountsQuery } = useAtomValue(
    $accountsByIdQueryLazy,
  );
  const [form] = useAtom($salesOrderForm);
  const setSalesOrderForm = useSetAtom($setSalesOrderForm);
  const resetForm = useSetAtom($resetSalesOrderForm);
  const validateForm = useSetAtom($validateSalesOrderForm);
  const validateFormDraft = useSetAtom($validateSalesOrderFormDraft);
  const updateSalesOrderMutation = useSetAtom($updateSalesOrder);

  // Other data hooks
  const toast = useToast();

  // Derived values
  const isDraftStatus = salesOrder?.status === enumSalesOrderStatus.Draft;
  const isLoading = isUpdating || isSaving;

  // Memoized values
  const selectedAccount = React.useMemo(
    () =>
      accountsQueryData?.find(
        (account) => account.id === salesOrder?.account?.id,
      ),
    [accountsQueryData, salesOrder?.account?.id],
  );

  const isFormChanged = React.useMemo(
    () =>
      originalFormState &&
      JSON.stringify(form) !== JSON.stringify(originalFormState),
    [form, originalFormState],
  );

  // Callbacks
  const handleSave = React.useCallback(async () => {
    if (!salesOrder) return;

    const validation =
      isMissingRequired && isDraftStatus ? validateFormDraft() : validateForm();
    if (!validation.isValid) {
      toast.error('Please fix validation errors before saving');
      return;
    }

    try {
      setIsUpdating(true);
      setSalesOrderForm({ isSubmitting: true });

      const updateData = {
        id: salesOrder.id,
        documentNumber: form.salesOrderNumber || undefined,
        type: enumSalesOrderType.International,
        startDate: form.salesOrderStartDate,
        endDate: form.salesOrderEndDate,
        accountId: form.customerId || undefined,
        counterpartyReferenceNumber:
          form.counterpartyReferenceNumber || undefined,
        contactId: form.contactId || undefined,
        facilityId: form.facilityId || undefined,
        accountRepresentativeId: form.accountRepresentativeId || undefined,
        shippingAddress: form.shippingAddress
          ? {
              addressType: form.shippingAddress.addressType as AddressType,
              streetAddress: form.shippingAddress.streetAddress,
              city: form.shippingAddress.city,
              state: form.shippingAddress.state,
              postalCode: form.shippingAddress.postalCode,
              country: form.shippingAddress.country,
              isMailingAddress: true,
            }
          : undefined,
        billingAddress: form.billingAddress
          ? {
              addressType: form.billingAddress.addressType as AddressType,
              streetAddress: form.billingAddress.streetAddress,
              city: form.billingAddress.city,
              state: form.billingAddress.state,
              postalCode: form.billingAddress.postalCode,
              country: form.billingAddress.country,
              isMailingAddress: true,
            }
          : undefined,
        paymentCurrency: form.paymentCurrency || undefined,
        freightTerm: form.freightTerm || undefined,
        paymentTerm: form.paymentTerm || undefined,
        doNotShip: form.doNotShip,
        recyclerExternalNotes: form.externalNotes || undefined,
        recyclerInternalNotes: form.internalNotes || undefined,
        completionNotes: form.completionNotes || undefined,
        bookingIds: form.bookings?.map((booking) => booking.id) ?? [],
      };

      await updateSalesOrderMutation(updateData);

      toast.success(t.salesOrderDetail.messages.updateSuccess);

      setOriginalFormState({ ...form });

      onSaveUpdates?.();
    } catch (error) {
      toast.error(t.salesOrderDetail.messages.updateError);
      console.error('Failed to update sales order:', error);
    } finally {
      setSalesOrderForm({ isSubmitting: false });
      setIsUpdating(false);
    }
  }, [
    salesOrder,
    isMissingRequired,
    isDraftStatus,
    validateFormDraft,
    validateForm,
    toast,
    setSalesOrderForm,
    form,
    updateSalesOrderMutation,
    t,
    onSaveUpdates,
  ]);

  const handleDiscard = React.useCallback(() => {
    if (originalFormState) {
      setSalesOrderForm(originalFormState);
    } else {
      resetForm();
    }
    onDiscard?.();
  }, [originalFormState, setSalesOrderForm, resetForm, onDiscard]);

  // Effects
  /** Syncs sales order data to form state */
  React.useEffect(() => {
    if (salesOrder) {
      const formState = salesOrderToFormState(salesOrder);
      setSalesOrderForm(formState);
      setOriginalFormState(formState);
    } else {
      resetForm();
      setOriginalFormState(null);
    }
  }, [salesOrder, setSalesOrderForm, resetForm]);

  /** Fetches account details when sales order account changes */
  React.useEffect(() => {
    if (salesOrder?.account?.id) {
      fetchAccountsQuery({
        accountIds: [salesOrder.account.id],
      });
    }
  }, [salesOrder?.account?.id, fetchAccountsQuery]);

  /** Matches shipping/billing addresses to account locations */
  React.useEffect(() => {
    if (salesOrder && form.customerId === salesOrder.account.id) {
      const addresses = selectedAccount?.addresses ?? [];

      const matchingShipToAddress = addresses.find(
        (addr) =>
          addr.address.streetAddress ===
            salesOrder.shippingAddress?.streetAddress &&
          addr.address.city === salesOrder.shippingAddress?.city &&
          addr.address.state === salesOrder.shippingAddress?.state &&
          addr.address.postalCode === salesOrder.shippingAddress?.postalCode,
      );

      const matchingBillToAddress = addresses.find(
        (addr) =>
          addr.address.streetAddress ===
            salesOrder.billingAddress?.streetAddress &&
          addr.address.city === salesOrder.billingAddress?.city &&
          addr.address.state === salesOrder.billingAddress?.state &&
          addr.address.postalCode === salesOrder.billingAddress?.postalCode,
      );

      const updates: Partial<SalesOrderFormState> = {};

      if (matchingShipToAddress) {
        updates.shipToLocationId = matchingShipToAddress.id;
      }

      if (matchingBillToAddress) {
        updates.billToLocationId = matchingBillToAddress.id;
      }

      if (Object.keys(updates).length > 0) {
        setSalesOrderForm(updates);
      }
    }
  }, [salesOrder, form.customerId, setSalesOrderForm, selectedAccount]);

  return (
    <SalesDetailLayout
      className="@SalesOrderTabContractInfo"
      variant={variant}
      footer={
        !disabled && (
          <div className="flex flex-shrink-0 items-center gap-3">
            <Button
              variant="outline"
              onPress={handleDiscard}
              isDisabled={isLoading}
            >
              {t.salesOrderDetail.actions.discard}
            </Button>
            <Button
              variant="solid"
              color="primary"
              onPress={handleSave}
              isPending={isLoading}
              isLoading={isLoading}
              isDisabled={!isFormChanged || !salesOrder}
            >
              {isMissingRequired && isDraftStatus
                ? t.salesOrderDetail.actions.saveDraft
                : t.salesOrderDetail.actions.saveUpdates}
            </Button>
          </div>
        )
      }
    >
      <SalesOrderWizardStepContractInfo disabled={disabled} />
    </SalesDetailLayout>
  );
};
